import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import Hidden from "@material-ui/core/Hidden";
import {
  FolderSpecial,
  PictureAsPdf,
  HelpOutline,
  Fullscreen,
  FullscreenExit,
} from "@material-ui/icons";
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import ActionBar from "components/ActionBar/ActionBar";
import { grayColor } from "assets/jss/material-dashboard-pro-react";
import { Grid } from "react-loader-spinner";
import useThemeColors from "../../hooks/useThemeColors";
import {
  Navigation,
  OverviewSection,
  HighlightsSection,
  ByDimensionSection,
  TopClientsSection,
  TopProductsSection,
  InitiativesSection,
  SessionManagerModal,
  LoadingPlaceholder,
  alignmentSessionStyles,
  defaultSessionPages,
  getAvailablePeriodOptions,
  convertPeriodsToSet,
  PERIOD_DEFAULT,
  TRANSITION_DURATION,
} from "./alignment_session_components";
import { useDataOrchestrator } from "./alignment_session_components/hooks";
import { getSessions, getPnlMetadata } from "services/governanceService";
import {
  sortPagesByOrder,
  findPageIndex,
  getMaxPageId,
  getMaxPageOrder,
  formatSessionDataPeriod,
  normalizePeriodType,
} from "./alignment_session_components/utils";

const useStyles = makeStyles(alignmentSessionStyles);

export default function AlignmentSession() {
  const classes = useStyles();
  const { t } = useTranslation();
  const { clientColor } = useThemeColors();

  const [sessionPages, setSessionPages] = useState(defaultSessionPages);
  const [activePage, setActivePage] = useState(
    () => sessionPages[0]?.id || null,
  );
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [periodReference, setPeriodReference] = useState(PERIOD_DEFAULT);
  const [highlightEnabled, setHighlightEnabled] = useState(true);
  const [focusMode, setFocusMode] = useState(false);
  const [sessionManagerOpen, setSessionManagerOpen] = useState(false);
  const [preloadedSessions, setPreloadedSessions] = useState(null);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [activeSession, setActiveSession] = useState(null);
  const [activeSessionLoading, setActiveSessionLoading] = useState(true);
  const [pnlMetadata, setPnlMetadata] = useState(null);
  const [pnlMetadataLoading, setPnlMetadataLoading] = useState(true);
  // Loading overlay: shown while the current page's data is not yet ready.
  // Stays mounted briefly after data loads to allow the fade-out animation.
  const [loadingOverlay, setLoadingOverlay] = useState(false);
  // Tracks whether we've shown page content at least once after the
  // initial load.  While false, the "carregando sessão" spinner stays
  // visible (so the user never sees a separate "carregando página" flash).
  const [hasShownContent, setHasShownContent] = useState(false);

  const dataOrchestrator = useDataOrchestrator({
    activeSession,
    pnlMetadata,
    periodReference,
  });

  const sortedPages = useMemo(
    () => sortPagesByOrder(sessionPages),
    [sessionPages],
  );

  // Convert raw period items from pnlMetadata into a Set for O(1) lookups
  const availablePeriodsSet = useMemo(
    () => convertPeriodsToSet(pnlMetadata?.filters?.items?.period),
    [pnlMetadata],
  );

  // Build period options with isDisabled flag based on data availability
  const periodOptions = useMemo(
    () =>
      getAvailablePeriodOptions(
        availablePeriodsSet,
        activeSession?.year,
        activeSession?.period_value,
        t,
      ),
    [availablePeriodsSet, activeSession?.year, activeSession?.period_value, t],
  );

  // Spinner principal – visível até a sessão, metadados E dados da primeira
  // página estarem prontos.  Depois que o conteúdo aparece pela primeira vez
  // (hasShownContent = true), navegações subsequentes usam o overlay de página.
  const isInitialLoading =
    activeSessionLoading ||
    pnlMetadataLoading ||
    (!!activeSession && !!pnlMetadata && !hasShownContent);

  useEffect(() => {
    const loadInitialData = async () => {
      setSessionsLoading(true);
      setActiveSessionLoading(true);
      setPnlMetadataLoading(true);

      try {
        const [sessionsData, pnlData] = await Promise.all([
          getSessions(),
          getPnlMetadata(),
        ]);

        setPreloadedSessions(sessionsData);
        const active = sessionsData.find((s) => s.is_active);
        setActiveSession(active || null);
        if (active?.period_type) {
          setPeriodReference(normalizePeriodType(active.period_type));
        }
        setPnlMetadata(pnlData);
      } catch (error) {
        setPreloadedSessions(null);
        setActiveSession(null);
        setPnlMetadata(null);
      } finally {
        setSessionsLoading(false);
        setActiveSessionLoading(false);
        setPnlMetadataLoading(false);
      }
    };

    loadInitialData();
  }, []);

  const addPage = useCallback((key) => {
    setSessionPages((prev) => {
      const maxId = getMaxPageId(prev);
      const maxOrder = getMaxPageOrder(prev);
      return [...prev, { id: maxId + 1, key, order: maxOrder + 1 }];
    });
  }, []);

  const removePage = useCallback(
    (pageId) => {
      setSessionPages((prev) => prev.filter((p) => p.id !== pageId));
      if (activePage === pageId) {
        const remaining = sessionPages.filter((p) => p.id !== pageId);
        setActivePage(remaining.length > 0 ? remaining[0].id : null);
      }
    },
    [activePage, sessionPages],
  );

  const updatePageKey = useCallback((pageId, newKey) => {
    setSessionPages((prev) =>
      prev.map((p) => (p.id === pageId ? { ...p, key: newKey } : p)),
    );
  }, []);

  const reorderPages = useCallback((orderedIds) => {
    setSessionPages((prev) =>
      prev.map((p) => ({
        ...p,
        order: orderedIds.indexOf(p.id) + 1,
      })),
    );
  }, []);

  const movePageUp = useCallback((pageId) => {
    setSessionPages((prev) => {
      const sorted = sortPagesByOrder(prev);
      const index = findPageIndex(sorted, pageId);
      if (index <= 0) return prev;

      const targetOrder = sorted[index].order;
      const swapOrder = sorted[index - 1].order;

      return prev.map((p) => {
        if (p.id === pageId) return { ...p, order: swapOrder };
        if (p.id === sorted[index - 1].id) return { ...p, order: targetOrder };
        return p;
      });
    });
  }, []);

  const movePageDown = useCallback((pageId) => {
    setSessionPages((prev) => {
      const sorted = sortPagesByOrder(prev);
      const index = findPageIndex(sorted, pageId);
      if (index < 0 || index >= sorted.length - 1) return prev;

      const targetOrder = sorted[index].order;
      const swapOrder = sorted[index + 1].order;

      return prev.map((p) => {
        if (p.id === pageId) return { ...p, order: swapOrder };
        if (p.id === sorted[index + 1].id) return { ...p, order: targetOrder };
        return p;
      });
    });
  }, []);

  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      window._alignmentSession = {
        addPage,
        removePage,
        updatePageKey,
        reorderPages,
        movePageUp,
        movePageDown,
        getPages: () => sessionPages,
        getPeriod: () => periodReference,
      };
    }
    return () => {
      if (process.env.NODE_ENV === "development") {
        delete window._alignmentSession;
      }
    };
  }, [
    addPage,
    removePage,
    updatePageKey,
    reorderPages,
    movePageUp,
    movePageDown,
    sessionPages,
    periodReference,
  ]);

  const handleOpenSessions = useCallback(() => {
    setSessionManagerOpen(true);
  }, []);

  const handleCloseSessionManager = useCallback(() => {
    setSessionManagerOpen(false);
  }, []);

  const handleSessionsChanged = useCallback(
    async (editedSessionId) => {
      try {
        const data = await getSessions();
        setPreloadedSessions(data);

        // Only update activeSession if the edited session is the currently active one
        if (editedSessionId && activeSession?.id === editedSessionId) {
          const updated = data.find((s) => s.id === editedSessionId);
          setActiveSession(updated || null);
        }
      } catch (error) {
        // Silently fail
      }
    },
    [activeSession?.id],
  );

  const refreshActiveSession = useCallback(async () => {
    if (!activeSession?.id) return;
    try {
      const data = await getSessions();
      setPreloadedSessions(data);
      const updated = data.find((s) => s.id === activeSession.id);
      setActiveSession(updated || null);
    } catch (error) {
      // Silently fail
    }
  }, [activeSession?.id]);

  const handleConfirmSessionSelection = useCallback(
    (sessionId) => {
      if (!preloadedSessions) return;
      const session = preloadedSessions.find((s) => s.id === sessionId);
      setActiveSession(session || null);
      if (session?.period_type) {
        setPeriodReference(normalizePeriodType(session.period_type));
      }
      setSessionManagerOpen(false);
    },
    [preloadedSessions],
  );

  const handleFocusMode = useCallback(() => {
    setFocusMode((prev) => !prev);
  }, []);

  const changePage = useCallback(
    (newPageId) => {
      if (newPageId === activePage || isTransitioning) return;
      setIsTransitioning(true);
      setTimeout(() => {
        setActivePage(newPageId);
        setTimeout(() => {
          setIsTransitioning(false);
        }, 250);
      }, TRANSITION_DURATION);
    },
    [activePage, isTransitioning],
  );

  const handlePageClick = useCallback(
    (pageId) => {
      changePage(pageId);
    },
    [changePage],
  );

  const handlePrevious = useCallback(() => {
    const currentIndex = findPageIndex(sortedPages, activePage);
    if (currentIndex > 0) {
      changePage(sortedPages[currentIndex - 1].id);
    }
  }, [activePage, sortedPages, changePage]);

  const handleNext = useCallback(() => {
    const currentIndex = findPageIndex(sortedPages, activePage);
    if (currentIndex < sortedPages.length - 1) {
      changePage(sortedPages[currentIndex + 1].id);
    }
  }, [activePage, sortedPages, changePage]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && focusMode) {
        setFocusMode(false);
        return;
      }

      const activeElement = document.activeElement;
      const tagName = activeElement?.tagName?.toLowerCase();
      const isInsideDialog = activeElement?.closest('[role="dialog"]');
      const isEditable =
        tagName === "input" ||
        tagName === "textarea" ||
        activeElement?.isContentEditable ||
        activeElement?.closest(".rdw-editor-main");

      const visibleDialog = document.querySelector(
        '.MuiDialog-root:not([aria-hidden="true"])',
      );

      if (isEditable || isInsideDialog || visibleDialog) return;

      if (event.key === "ArrowLeft") {
        handlePrevious();
      } else if (event.key === "ArrowRight") {
        handleNext();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handlePrevious, handleNext, focusMode]);

  const currentPage = useMemo(
    () => sortedPages.find((p) => p.id === activePage),
    [sortedPages, activePage],
  );

  // Is the current page's data ready (according to the orchestrator)?
  const isCurrentPageReady = useMemo(() => {
    if (!activeSession || !pnlMetadata || !currentPage) return false;
    return dataOrchestrator?.isPageReady(currentPage.key) ?? false;
  }, [activeSession, pnlMetadata, currentPage, dataOrchestrator]);

  // Once the first page is ready, mark content as shown so the initial
  // spinner can be replaced by the normal page overlay on future navigations.
  useEffect(() => {
    if (!hasShownContent && isCurrentPageReady) {
      setHasShownContent(true);
    }
  }, [hasShownContent, isCurrentPageReady]);

  // Reset when the active session changes (user picks a different session).
  useEffect(() => {
    setHasShownContent(false);
  }, [activeSession?.id]);

  // Notify orchestrator whenever the active page (or session/metadata) changes
  // so it can (re-)prioritise the prefetch queue.
  useEffect(() => {
    if (!activeSession || !pnlMetadata || !currentPage) return;
    const pageKeys = sortedPages.map((p) => p.key);
    dataOrchestrator?.setCurrentPage(currentPage.key, pageKeys);
  }, [activeSession, pnlMetadata, currentPage, sortedPages, dataOrchestrator]);

  // Handle period change: batch the overlay activation with the period state
  // update so both take effect in the SAME render — no 1-frame gap where
  // sections would show internal spinners before the overlay appears.
  const handlePeriodChange = useCallback(
    (newPeriod) => {
      if (newPeriod === periodReference) return;
      setLoadingOverlay(true);
      setPeriodReference(newPeriod);
    },
    [periodReference],
  );

  // `showLoadingOverlay` is derived synchronously.
  // `loadingOverlay` state keeps the component mounted during fade-out.
  const showLoadingOverlay =
    hasShownContent && (!isCurrentPageReady || loadingOverlay);

  const showHighlightToggle = useMemo(
    () =>
      currentPage?.key === "by_dimension" ||
      currentPage?.key === "top_customers" ||
      currentPage?.key === "top_products",
    [currentPage],
  );

  const actionOptions = useMemo(
    () => (
      <>
        <GridItem className={classes.actionWrapperOuter}>
          <div
            className={classes.actionWrapper}
            onClick={handleOpenSessions}
            style={{ cursor: "pointer" }}
            role="button"
            aria-label="Sessões"
            tabIndex={0}
          >
            <div className={classes.actionIcon}>
              <FolderSpecial />
            </div>
            <h6 className={classes.actionText}>
              {t("alignment_session.actions.sessions")}
            </h6>
          </div>
        </GridItem>

        <GridItem className={classes.actionWrapperOuter}>
          <div
            className={classes.actionWrapper}
            style={{ cursor: "pointer", opacity: 0.5 }}
          >
            <div className={classes.actionIcon}>
              <PictureAsPdf />
            </div>
            <h6 className={classes.actionText}>
              {t("alignment_session.actions.generate_pdf")}
            </h6>
          </div>
        </GridItem>

        <GridItem className={classes.actionWrapperOuter}>
          <div
            className={classes.actionWrapper}
            style={{ cursor: "pointer", opacity: 0.5 }}
          >
            <div className={classes.actionIcon}>
              <HelpOutline />
            </div>
            <h6 className={classes.actionText}>
              {t("alignment_session.actions.help")}
            </h6>
          </div>
        </GridItem>

        <GridItem className={classes.actionWrapperOuter}>
          <div
            className={classes.actionWrapper}
            onClick={
              isInitialLoading || !activeSession ? undefined : handleFocusMode
            }
            style={{
              cursor: "pointer",
              opacity: isInitialLoading || !activeSession ? 0.5 : 1,
            }}
          >
            <div className={classes.actionIcon}>
              {focusMode ? <FullscreenExit /> : <Fullscreen />}
            </div>
            <h6 className={classes.actionText}>
              {t("alignment_session.actions.focus_mode")}
            </h6>
          </div>
        </GridItem>
      </>
    ),
    [
      classes,
      t,
      focusMode,
      handleOpenSessions,
      handleFocusMode,
      isInitialLoading,
      activeSession,
    ],
  );

  const sectionProps = useMemo(
    () => ({
      periodReference,
      clientColor,
      activeSession,
      activeSessionLoading,
      pnlMetadata,
      pnlMetadataLoading,
      dataOrchestrator,
    }),
    [
      periodReference,
      clientColor,
      activeSession,
      activeSessionLoading,
      pnlMetadata,
      pnlMetadataLoading,
      dataOrchestrator,
    ],
  );

  const renderPageContent = useCallback(
    (isFocusMode = false) => {
      if (!currentPage) return null;

      // Render actual content
      let content = null;
      switch (currentPage.key) {
        case "overview":
          content = (
            <OverviewSection
              {...sectionProps}
              onSessionUpdate={refreshActiveSession}
              focusMode={isFocusMode}
            />
          );
          break;
        case "highlights":
          content = (
            <HighlightsSection {...sectionProps} focusMode={isFocusMode} />
          );
          break;
        case "by_dimension":
          content = (
            <ByDimensionSection
              {...sectionProps}
              highlightEnabled={highlightEnabled}
              focusMode={isFocusMode}
            />
          );
          break;
        case "top_customers":
          content = (
            <TopClientsSection
              {...sectionProps}
              highlightEnabled={highlightEnabled}
              focusMode={isFocusMode}
            />
          );
          break;
        case "top_products":
          content = (
            <TopProductsSection
              {...sectionProps}
              highlightEnabled={highlightEnabled}
              focusMode={isFocusMode}
            />
          );
          break;
        case "initiatives":
          content = (
            <InitiativesSection {...sectionProps} focusMode={isFocusMode} />
          );
          break;
        default:
          content = (
            <Card className={classes.singlePageCard}>
              <CardBody className={classes.cardContent}>
                <h3
                  className={classes.contentTitle}
                  style={{ color: clientColor }}
                >
                  {t(`alignment_session.topics.${currentPage.key}`)}
                </h3>
                <p className={classes.contentSubtitle}>
                  {t("alignment_session.content_placeholder", {
                    page: t(`alignment_session.topics.${currentPage.key}`),
                  })}
                </p>
              </CardBody>
            </Card>
          );
      }

      // Render content with loading overlay on top.
      // The overlay is shown when loadingOverlay is true (page data not ready).
      // When isCurrentPageReady becomes true, we pass onExitComplete so the
      // LoadingPlaceholder can fade out gracefully before being unmounted.
      return (
        <div style={{ position: "relative" }}>
          {content}
          {showLoadingOverlay && (
            <LoadingPlaceholder
              clientColor={clientColor}
              onExitComplete={
                isCurrentPageReady ? () => setLoadingOverlay(false) : null
              }
              focusMode={isFocusMode}
            />
          )}
        </div>
      );
    },
    [
      currentPage,
      sectionProps,
      highlightEnabled,
      refreshActiveSession,
      classes,
      clientColor,
      t,
      showLoadingOverlay,
      isCurrentPageReady,
    ],
  );

  const pageContent = useMemo(
    () => renderPageContent(false),
    [renderPageContent],
  );
  const focusModePageContent = useMemo(
    () => renderPageContent(true),
    [renderPageContent],
  );

  const navigationProps = useMemo(
    () => ({
      sortedPages,
      activePage,
      onPageClick: handlePageClick,
      onPrevious: handlePrevious,
      onNext: handleNext,
      clientColor,
      periodOptions,
      periodReference,
      onPeriodChange: handlePeriodChange,
      highlightEnabled,
      onHighlightChange: setHighlightEnabled,
      showHighlightToggle,
      dataOrchestrator,
    }),
    [
      sortedPages,
      activePage,
      handlePageClick,
      handlePrevious,
      handleNext,
      clientColor,
      periodOptions,
      periodReference,
      handlePeriodChange,
      highlightEnabled,
      showHighlightToggle,
      dataOrchestrator,
    ],
  );

  // Loading state: only for initial session/metadata load
  // Data orchestrator loading will be handled separately (spinners per section)
  const dataPeriod = formatSessionDataPeriod(activeSession, t);

  return (
    <div>
      {activeSession && dataPeriod && (
        <p className={classes.dataUpdateText}>
          <strong>{activeSession.name}</strong>
          {" - "}
          {t("alignment_session.data_updated_until")}{" "}
          <strong>{dataPeriod}</strong>
        </p>
      )}

      <Hidden smDown>
        <ActionBar actions={actionOptions} spinnerIn={false} />
      </Hidden>

      <GridContainer style={{ marginTop: "-8px" }}>
        <Hidden mdUp>
          <GridItem xs={12}>
            <ActionBar actions={actionOptions} spinnerIn={false} sm />
          </GridItem>
        </Hidden>

        <GridItem xs={12}>
          {!activeSession ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "calc(100vh - 125px)",
                width: "100%",
                backgroundColor: grayColor[11],
                borderRadius: "8px",
                position: "relative",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "20px",
                  position: "absolute",
                  opacity: activeSessionLoading || pnlMetadataLoading ? 1 : 0,
                  transition: "opacity 300ms ease-in-out",
                  pointerEvents:
                    activeSessionLoading || pnlMetadataLoading
                      ? "auto"
                      : "none",
                  paddingBottom: "50px",
                  zIndex: 20,
                }}
              >
                <span style={{ fontSize: "16px" }}>
                  {t("alignment_session.loading_session")}
                </span>
                <Grid width={40} height={40} color={clientColor} />
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "16px",
                  position: "absolute",
                  opacity: !activeSessionLoading && !pnlMetadataLoading ? 1 : 0,
                  transition: "opacity 300ms ease-in-out",
                  pointerEvents:
                    !activeSessionLoading && !pnlMetadataLoading
                      ? "auto"
                      : "none",
                  zIndex: 10,
                }}
              >
                <FolderSpecial
                  style={{ fontSize: "64px", color: grayColor[0] }}
                />
                <span
                  style={{
                    color: grayColor[1],
                    fontSize: "24px",
                    fontWeight: "300",
                  }}
                >
                  {t("alignment_session.no_session_selected")}
                </span>
                <span
                  style={{
                    color: grayColor[0],
                    marginTop: "12px",
                    fontSize: "14px",
                    textAlign: "center",
                    maxWidth: "400px",
                  }}
                >
                  {t("alignment_session.select_session_hint")}
                </span>
              </div>
            </div>
          ) : (
            <div style={{ position: "relative" }}>
              {/* Navigation and content - ALWAYS rendered (even during loading) */}
              <Navigation {...navigationProps} />
              <div
                className={`${classes.pageContentWrapper} ${
                  isTransitioning ? classes.pageContentFading : ""
                }`}
              >
                {pageContent}
              </div>

              {/* Main loading spinner - covers everything until ALL data loads */}
              {isInitialLoading && (
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "calc(100vh - 125px)",
                    width: "100%",
                    backgroundColor: grayColor[11],
                    borderRadius: "8px",
                    zIndex: 30,
                  }}
                >
                  <span style={{ fontSize: "16px", marginBottom: "20px" }}>
                    {t("alignment_session.loading_session")}
                  </span>
                  <Grid width={40} height={40} color={clientColor} />
                </div>
              )}
            </div>
          )}
        </GridItem>
      </GridContainer>

      <SessionManagerModal
        open={sessionManagerOpen}
        onClose={handleCloseSessionManager}
        clientColor={clientColor}
        preloadedSessions={preloadedSessions}
        sessionsLoading={sessionsLoading}
        onSessionsChanged={handleSessionsChanged}
        onConfirmSelection={handleConfirmSessionSelection}
        pnlMetadata={pnlMetadata}
        availablePeriodsSet={availablePeriodsSet}
      />

      <div
        className={`${classes.focusModeOverlay} ${
          focusMode ? classes.focusModeOverlayActive : ""
        }`}
      >
        <div className={classes.focusModeContent}>
          <Navigation
            {...navigationProps}
            focusMode={focusMode}
            onFocusModeToggle={handleFocusMode}
          />

          <div
            className={`${classes.pageContentWrapper} ${classes.focusModePageWrapper} ${
              isTransitioning ? classes.pageContentFading : ""
            }`}
          >
            {focusModePageContent}
          </div>

          {activeSession && dataPeriod && (
            <p className={classes.dataUpdateTextFocusMode}>
              <strong>{activeSession.name}</strong>
              {" - "}
              {t("alignment_session.data_updated_until")}{" "}
              <strong>{dataPeriod}</strong>
            </p>
          )}

          <div className={classes.focusModeFooter}>
            &copy; {new Date().getFullYear()}{" "}
            <a
              href="https://www.integrationconsulting.com"
              target="_blank"
              rel="noreferrer"
            >
              Integration Consulting
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
